import React from 'react';
import AreaListPanel from '../components/AreaListPanel';
import MultiCheckPanel from '../components/MultiCheckPanel';
import ProductTablePanel from '../components/ProductTablePanel';
import SlotTablePanel from '../components/SlotTablePanel';
import { t } from '../i18n';

// V6 — One-question-at-a-time, dispatch theo question.type:
//   - default        → Yes/No binary
//   - single_pick    → chọn 1 trong nhiều đáp án, khai ở question.choices:
//                      [{ value, label:{vi,en,...}, hint?:{vi,en,...} }]
//                      Dùng cho setting có nhiều hơn hai giá trị (phương pháp tính thuế
//                      chẳng hạn) — trước đây phải bẻ thành chuỗi câu Có/Không khó hiểu.
//                      valueMapping tra theo `value` dạng chuỗi, engine đã hỗ trợ sẵn.
//   - question.detail → { yes, no, note } (mỗi phần là {vi,en,...}): điều xảy ra khi chọn
//                      Có / Không. Mặc định thu gọn sau một dòng bấm để mở; BE chỉ gửi
//                      cho câu cần cân nhắc.
// Parent quản answers + answerHistory + viewIdx + cascade.
// Bấm chỉ set answer (không advance). Next button validate currentAnswer !== undefined.
export default function Stage2Round(props) {
    var locale = props.locale || 'vi';
    var question = props.question || null;
    var currentAnswer = props.currentAnswer;       // 0 | 1 | string | undefined
    var positionLabel = props.positionLabel || 1;
    var totalEstimate = props.totalEstimate || 0;
    var canBack = !!props.canBack;
    var canNext = !!props.canNext;
    var panelData = props.panelData;
    var onPanelChange = props.onPanelChange;

    // Yes/No: auto-advance khi pending, set-only khi revisit (xử lý trong onPickAndNext của parent).
    function pickAndNext(val) {
        if (!question) return;
        if (typeof props.onPickAndNext === 'function') props.onPickAndNext(question.id, val);
        else if (typeof props.onPick === 'function') props.onPick(question.id, val);
    }

    function onNext() {
        if (!canNext) return;
        if (typeof props.onNext === 'function') props.onNext();
    }

    function onBack() {
        if (typeof props.onBack === 'function') props.onBack();
    }

    var yesLabel = t(locale, 'answer_yes');
    var noLabel = t(locale, 'answer_no');
    var backLabel = t(locale, 'cta_back');
    var nextLabel = t(locale, 'cta_continue');
    var hintNeedAnswer = t(locale, 'need_answer');

    // Mỗi câu mở lại ở trạng thái thu gọn.
    var [detailOpen, setDetailOpen] = React.useState(false);
    React.useEffect(function () { setDetailOpen(false); }, [question ? question.id : null]);

    if (!question) {
        return <div className="wizq wizq--empty" />;
    }

    var pickText = function (obj) {
        if (!obj) return '';
        if (typeof obj !== 'object') return String(obj);
        return obj[locale] || obj.en || obj.vi || '';
    };
    var qText = pickText(question.question) || question.id;
    var hText = pickText(question.hint);
    var detail = question.detail || null;
    var detailYes = detail ? pickText(detail.yes) : '';
    var detailNo = detail ? pickText(detail.no) : '';
    var detailNote = detail ? pickText(detail.note) : '';
    var hasDetail = !!(detailYes || detailNo);

    var isAreas = question.type === 'qty_with_prefix';
    var isMultiPick = question.type === 'multi_pick';
    var isSinglePick = question.type === 'single_pick';
    var choices = (isSinglePick && question.choices instanceof Array) ? question.choices : [];
    var isCustomPanel = question.type === 'custom_panel';
    var customPanelType = isCustomPanel && question.customPanel ? question.customPanel.type : null;
    var isProductTable = customPanelType === 'product_table_with_ai';
    var isSlotTable = customPanelType === 'slot_table_with_ai';

    return (
        <div className={'wizq'
            + (isAreas ? ' wizq--areas' : '')
            + (isMultiPick ? ' wizq--multi' : '')
            + (isSinglePick ? ' wizq--pick' : '')
            + (isProductTable ? ' wizq--products' : '')
            + (isSlotTable ? ' wizq--slots' : '')}>
            {/* <div className="wizq-meta">
                <span className="wizq-progress">{progressLabel}</span>
            </div> */}

            <div className="wizq-list">
                <div key={question.id} className="wizq-card wizq-card--single">
                    <div className="wizq-card__q">{qText}</div>
                    {hText ? <div className="wizq-card__hint">{hText}</div> : null}

                    {isMultiPick ? (
                        <MultiCheckPanel
                            locale={locale}
                            config={question.customPanel}
                            value={panelData}
                            onChange={function (v) { onPanelChange(question.id, v); }}
                        />
                    ) : isProductTable ? (
                        <ProductTablePanel
                            key={question.id}
                            allowSampleData
                            locale={locale}
                            config={question.customPanel}
                            value={panelData}
                            onChange={function (v) { onPanelChange(question.id, v); }}
                            aiReady={props.aiProviderReady !== false}
                        />
                    ) : isSlotTable ? (
                        <SlotTablePanel
                            locale={locale}
                            config={question.customPanel}
                            value={panelData}
                            onChange={function (v) { onPanelChange(question.id, v); }}
                            aiReady={props.aiProviderReady !== false}
                            currency={props.currency}
                        />
                    ) : isSinglePick ? (
                        <div className="wizq-card__opts wizq-card__opts--stack">
                            {choices.map(function (c) {
                                return (
                                    <button
                                        key={String(c.value)}
                                        type="button"
                                        className={'wizq-opt wizq-opt--pick' + (currentAnswer === c.value ? ' wizq-opt--on' : '')}
                                        onClick={function () { pickAndNext(c.value); }}
                                    >
                                        <span className="wizq-opt__label">{pickText(c.label)}</span>
                                        {c.hint ? <span className="wizq-opt__hint">{pickText(c.hint)}</span> : null}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <React.Fragment>
                            <div className="wizq-card__opts">
                                <button
                                    type="button"
                                    className={'wizq-opt' + (currentAnswer === 1 ? ' wizq-opt--on wizq-opt--yes' : '')}
                                    onClick={function () { pickAndNext(1); }}
                                >
                                    {yesLabel}
                                </button>
                                <button
                                    type="button"
                                    className={'wizq-opt' + (currentAnswer === 0 ? ' wizq-opt--on wizq-opt--no' : '')}
                                    onClick={function () { pickAndNext(0); }}
                                >
                                    {noLabel}
                                </button>
                            </div>
                            {hasDetail ? (
                                <div className={'wizq-detail' + (detailOpen ? ' wizq-detail--open' : '')}>
                                    <button
                                        type="button"
                                        className="wizq-detail__toggle"
                                        aria-expanded={detailOpen}
                                        onClick={function () { setDetailOpen(!detailOpen); }}
                                    >
                                        <i className="dx-icon dx-icon-help" />
                                        <span>{t(locale, 'detail_toggle')}</span>
                                        <i className={'dx-icon dx-icon-chevron' + (detailOpen ? 'up' : 'down')} />
                                    </button>
                                    {detailOpen ? (
                                        <div className="wizq-detail__body">
                                            {detailYes ? (
                                                <div className="wizq-detail__row">
                                                    <span className="wizq-detail__tag wizq-detail__tag--yes">{yesLabel}</span>
                                                    <span className="wizq-detail__text">{detailYes}</span>
                                                </div>
                                            ) : null}
                                            {detailNo ? (
                                                <div className="wizq-detail__row">
                                                    <span className="wizq-detail__tag wizq-detail__tag--no">{noLabel}</span>
                                                    <span className="wizq-detail__text">{detailNo}</span>
                                                </div>
                                            ) : null}
                                            {detailNote ? <div className="wizq-detail__note">{detailNote}</div> : null}
                                            <div className="wizq-detail__foot">
                                                <i className="dx-icon dx-icon-preferences" />
                                                <span>{t(locale, 'detail_changeable')}</span>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            {isAreas && currentAnswer === 1 ? (
                                <AreaListPanel
                                    locale={locale}
                                    config={question.customPanel}
                                    value={panelData}
                                    onChange={function (v) { onPanelChange(question.id, v); }}
                                />
                            ) : null}
                        </React.Fragment>
                    )}
                </div>
            </div>

            <div className="wizq-cta">
                {canBack ? (
                    <button
                        type="button"
                        className="wizq-btn wizq-btn--ghost"
                        onClick={onBack}
                    >
                        {backLabel}
                    </button>
                ) : <span className="wizq-btn-spacer" />}
                <button
                    type="button"
                    className="wizq-btn wizq-btn--primary"
                    onClick={onNext}
                    disabled={!canNext}
                    title={!canNext ? hintNeedAnswer : ''}
                >
                    {nextLabel}
                </button>
            </div>
        </div>
    );
}
